// utils/pdfGenerator.js
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Add autoTable to jsPDF prototype
jsPDF.autoTable = autoTable;

/**
 * Convert image URL to base64 using CORS-enabled fetch
 */
const imageUrlToBase64 = async (url) => {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn(`Creating blob failed:`, error.message);
  }
};

/**
 * Preload and convert all images to base64 with CORS support
 */
const preloadImages = async (element) => {
  const images = element.querySelectorAll("img");

  const imagePromises = Array.from(images).map(async (img, index) => {
    if (img.src && !img.src.startsWith("data:")) {
      try {
        const base64 = await imageUrlToBase64(img.src);
        if (base64) {
          img.setAttribute("data-original-src", img.src);
          img.src = base64;
        } else {
          console.warn(
            `Failed to convert image ${index + 1} to base64, keeping original`
          );
          // Keep original src but mark it for potential issues
          img.setAttribute("data-conversion-failed", "true");
        }
      } catch (error) {
        console.error(`Failed to preload image ${index + 1}:`, img.src, error);
        img.setAttribute("data-conversion-failed", "true");
      }
    }
  });

  await Promise.all(imagePromises);
};

/**
 * Restore original image sources
 */
const restoreImages = (element) => {
  const images = element.querySelectorAll("img[data-original-src]");
  images.forEach((img) => {
    const originalSrc = img.getAttribute("data-original-src");
    if (originalSrc) {
      img.src = originalSrc;
      img.removeAttribute("data-original-src");
    }
  });
};

/**
 * Clone the element for PDF generation without affecting the original
 */
const cloneElementForPDF = (originalElement) => {
  // Create a container for the clone
  const cloneContainer = document.createElement("div");
  cloneContainer.style.position = "fixed";
  cloneContainer.style.left = "-9999px"; // Keep it off-screen
  cloneContainer.style.top = "0";
  cloneContainer.style.zIndex = "10000";
  cloneContainer.style.opacity = "1";
  cloneContainer.style.visibility = "visible";
  cloneContainer.style.pointerEvents = "none";
  cloneContainer.style.width = "210mm";
  cloneContainer.style.height = "auto";
  cloneContainer.style.background = "#ffffff";
  cloneContainer.style.padding = "20mm";
  cloneContainer.style.boxSizing = "border-box";

  // Clone the element with all content
  const clone = originalElement.cloneNode(true);
  const uniqueId = "pdf-template-clone-" + Date.now();
  clone.id = uniqueId;

  // Ensure clone is fully visible
  clone.style.visibility = "visible";
  clone.style.opacity = "1";
  clone.style.position = "relative";
  clone.style.left = "0";
  clone.style.top = "0";
  clone.style.width = "100%";
  clone.style.height = "auto";
  clone.style.background = "#ffffff";
  clone.style.display = "block";

  cloneContainer.appendChild(clone);
  document.body.appendChild(cloneContainer);

  return {
    container: cloneContainer,
    element: clone,
    id: uniqueId,
  };
};

/**
 * Remove cloned element after PDF generation
 */
const removeClonedElement = (cloneData) => {
  if (cloneData && cloneData.container && cloneData.container.parentNode) {
    cloneData.container.parentNode.removeChild(cloneData.container);
  }
};

/**
 * Extract and convert CSS styles from table for PDF
 */
const getTableStyles = (tableElement) => {
  const firstHeader = tableElement.querySelector("th");

  // Helper to parse CSS color
  const parseColor = (color) => {
    if (!color) return [203, 213, 224]; // Default #cbd5e0

    if (color.startsWith("#")) {
      const hex = color.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return [r, g, b];
    }

    if (color.startsWith("rgb")) {
      const match = color.match(/(\d+),\s*(\d+),\s*(\d+)/);
      if (match)
        return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
    }

    return [203, 213, 224]; // Default fallback
  };

  return {
    headerBg: parseColor(firstHeader?.style?.backgroundColor || "#f8f9fa"),
    borderColor: parseColor(firstHeader?.style?.borderColor || "#cbd5e0"),
    fontSize: parseInt(firstHeader?.style?.fontSize || "9"),
    padding: parseInt(firstHeader?.style?.padding?.replace("px", "") || "6"),
  };
};

/**
 * Generate PDF with intelligent page breaks and optimal space utilization
 */
export const generatePDFAdvanced = async (
  elementId,
  filename = "report.pdf"
) => {
  const originalElement = document.getElementById(elementId);
  if (!originalElement) {
    console.error("Element not found");
    return;
  }

  let cloneData = null;

  try {
    // Clone the element for PDF generation (keeps original hidden)
    cloneData = cloneElementForPDF(originalElement);
    const element = cloneData.element;

    // Small delay to ensure DOM updates
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Preload all images as base64
    await preloadImages(element);

    const PDF_WIDTH = 210;
    const PDF_HEIGHT = 297;
    const MARGIN = 10;
    const FOOTER_HEIGHT = 10; // Reserve space for page numbers
    const CONTENT_WIDTH = PDF_WIDTH - MARGIN * 2;
    const AVAILABLE_HEIGHT = PDF_HEIGHT - MARGIN * 2 - FOOTER_HEIGHT;

    const sections = element.querySelectorAll("[data-pdf-section]");

    if (sections.length === 0) {
      console.error("No sections found with data-pdf-section attribute");
      return;
    }

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    let yPosition = MARGIN;
    // let currentPage = 1;

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      // Wait for images to render
      const datasValue = section.getAttribute("data-pdf-section");
      let table = null;

      if (datasValue.includes("table")) {
        table = section?.querySelector("table");
      }

      const isNoMargin = datasValue.includes("nomargin");

      await new Promise((resolve) => setTimeout(resolve, 200));

      if (table) {
        const tableStyles = getTableStyles(table);
        // Handle table with jsPDF-AutoTable using standalone function

        // Use autoTable as a function, not as a method
        autoTable(pdf, {
          html: table,
          startY: yPosition,
          margin: { left: MARGIN, right: MARGIN },
          styles: {
            fontSize: tableStyles.fontSize,
            cellPadding: tableStyles.padding / 2, // Adjust for PDF
            lineColor: tableStyles.borderColor,
            lineWidth: 0.5,
            valign: "middle",
            halign: "center",
            fillColor: [255, 255, 255], // Force white background for ALL cells
            textColor: [0, 0, 0],
          },
          headStyles: {
            fillColor: tableStyles.headerBg,
            textColor: [0, 0, 0],
            fontStyle: "bold",
            lineColor: tableStyles.borderColor,
            lineWidth: 0.5,
          },
          bodyStyles: {
            fillColor: [255, 255, 255], // Force white background for ALL body cells
            textColor: [0, 0, 0],
            lineColor: tableStyles.borderColor,
            lineWidth: 0.3,
          },
          // Completely remove alternateRowStyles
          tableWidth: "auto",
          pageBreak: "auto",
          rowPageBreak: "avoid",
          // Add theme to ensure no alternating colors
          theme: "grid", // Use grid theme which doesn't have alternating colors
        });

        // Get the final yPosition after table rendering
        const tableFinalY = pdf.lastAutoTable?.finalY || yPosition;
        yPosition = tableFinalY + 5;
      } else {
        const canvas = await html2canvas(section, {
          scale: 1.8, // Reduced from 2 to make content slightly smaller
          useCORS: true,
          allowTaint: false,
          logging: false,
          backgroundColor: "#ffffff",
          imageTimeout: 15000,
          removeContainer: false,
          windowWidth: section.scrollWidth,
          windowHeight: section.scrollHeight,
          onclone: (clonedDoc) => {
            // Apply the same fixes as in the main generator
            const clonedImages = clonedDoc.querySelectorAll("img");
            clonedImages.forEach((img) => {
              img.style.display = "block";
              img.style.visibility = "visible";
              img.style.opacity = "1";
            });

            const rowSpanCells = clonedDoc.querySelectorAll(
              '[data-pdf-rowspan="true"]'
            );
            rowSpanCells.forEach((cell) => {
              cell.style.border = "none";
              cell.style.borderTop = "none";
              cell.style.borderRight = "none";
              cell.style.borderBottom = "none";
              cell.style.borderLeft = "none";
              cell.style.backgroundColor = "transparent";
            });
          },
        });

        const imgData = canvas.toDataURL("image/jpeg", 1);
        const imgWidth = CONTENT_WIDTH;

        // Validate canvas dimensions to prevent jsPDF errors
        if (
          !canvas.width ||
          !canvas.height ||
          canvas.width <= 0 ||
          canvas.height <= 0
        ) {
          console.warn(
            `Skipping section ${datasValue} - invalid canvas dimensions:`,
            {
              width: canvas.width,
              height: canvas.height,
            }
          );
          continue;
        }

        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Validate calculated dimensions
        if (!imgHeight || imgHeight <= 0 || !isFinite(imgHeight)) {
          console.warn(
            `Skipping section ${datasValue} - invalid calculated height:`,
            imgHeight
          );
          continue;
        }

        // Smart page break logic
        if (imgHeight > AVAILABLE_HEIGHT) {
          let remainingHeight = imgHeight;
          let sourceY = 0;
          let isFirstSectionPart = true;

          while (remainingHeight > 0) {
            const currentAvailableHeight = isFirstSectionPart
              ? AVAILABLE_HEIGHT - (yPosition - MARGIN)
              : AVAILABLE_HEIGHT;

            const heightToAdd = Math.min(
              remainingHeight,
              currentAvailableHeight
            );
            const sourceHeight = (heightToAdd / imgWidth) * canvas.width;

            // Add new page if this is not the first part of the section
            if (!isFirstSectionPart) {
              pdf.addPage();
              // currentPage++;
              yPosition = MARGIN;
            }

            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = canvas.width;
            tempCanvas.height = sourceHeight;
            const tempCtx = tempCanvas.getContext("2d");

            tempCtx.drawImage(
              canvas,
              0,
              sourceY,
              canvas.width,
              sourceHeight,
              0,
              0,
              canvas.width,
              sourceHeight
            );

            const tempImgData = tempCanvas.toDataURL("image/jpeg", 0.95);
            pdf.addImage(
              tempImgData,
              "JPEG",
              MARGIN,
              yPosition,
              imgWidth,
              heightToAdd
            );

            sourceY += sourceHeight;
            remainingHeight -= heightToAdd;
            yPosition += heightToAdd;
            isFirstSectionPart = false;
          }

          // Add small spacing after split section
          yPosition += 5;
        } else {
          // Check if section would fit with reasonable space utilization
          const wouldExceedPage = yPosition + imgHeight > PDF_HEIGHT - MARGIN;
          const remainingSpaceAfter =
            PDF_HEIGHT - MARGIN - (yPosition + imgHeight);
          const isReasonableToBreak = remainingSpaceAfter < 20; // Less than 20mm remaining

          if (wouldExceedPage || (imgHeight > 50 && isReasonableToBreak)) {
            // Start new page if section would exceed page OR if it's a large section and would leave little space
            pdf.addPage();
            // currentPage++;
            yPosition = MARGIN;

            pdf.addImage(
              imgData,
              "JPEG",
              MARGIN,
              yPosition,
              imgWidth,
              imgHeight
            );
            yPosition = isNoMargin
              ? yPosition + imgHeight
              : yPosition + imgHeight + 5; // Add spacing after section
          } else {
            // Section fits on current page with reasonable space
            pdf.addImage(
              imgData,
              "JPEG",
              MARGIN,
              yPosition,
              imgWidth,
              imgHeight
            );
            yPosition = isNoMargin
              ? yPosition + imgHeight
              : yPosition + imgHeight + 5; // Add spacing after section
          }
        }
      }
    }

    // Add page numbers to all pages
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(9);
      pdf.setTextColor(60, 60, 60);
      const pageText = `Page ${i} of ${totalPages}`;
      const textWidth = pdf.getTextWidth(pageText);
      pdf.text(
        pageText,
        (PDF_WIDTH - textWidth) / 2,
        PDF_HEIGHT - MARGIN + 5 // Position in footer area
      );
    }

    // Restore original image sources
    restoreImages(element);
    pdf.save(filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    // restoreImages(element);
    throw error;
  } finally {
    // Always remove the cloned element
    if (cloneData) {
      removeClonedElement(cloneData);
    }
  }
};
